import spartaEventsUtility from '@sparta.global.utilities/sparta-events-utility/dist/es6.js';
import spartaGlobalFunctionsUtility from '@sparta.global.utilities/sparta-global-functions-utility/dist/es6.js';
import spartaStoreUtility from '@sparta.global.utilities/sparta-store-utility/dist/es6.js';
import spartaTransportUtility from '@sparta.global.utilities/sparta-transport-utility/dist/es6.js';

/**
 * Namespace for the Sparta CMS Utility.
 * @constant {string}
 */
const utilityNamespace = 'spartaContentManagerUtility';

/**
 * Default paramets to be cosumed by this utility
 * @constant {string}
 */
const DEFAULTS = {
  cdn: {
    endpoint: 'spa-content/v1/content'
  },
  ATTRIBUTES: {
    contentKey: 'data-content-key',
    contentRecord: 'data-content-record-key',
    dataOptions: 'data-options',
    dataDynamicTokens: 'data-dynamic-tokens',
    disableAutoInject: 'spa-cms-inject-disable',
    dynamicDataHook: 'cms-dynamic-token'
  },
  EVENTS: {
    CMS_Utility_Key_Complete: `${utilityNamespace}.CMS_Utility_Key_Complete`
  },
  STATUS: {
    ready: 'ready',
    complete: 'complete',
    failed: 'failed',
    partialFailure: 'partialFailure'
  },
  STRINGS: {
    ERROR_MISSING_CONTAINER() {
      return 'sparta-content-manager-utility Content container not found. Update configuration to include a valid selector property';
    },
    ERROR_MISSING_SETTINGS() {
      return 'sparta-content-manager-utility Settings not configured';
    },
    ERROR_MISSING_REQUIRED_VARIABLES(required = '', provided = {}) {
      return `Missing the one or more of the following parameters: ${required}.\nGiven values: ${JSON.stringify(provided)}`;
    },
    ERROR_MISSING_SITE_HOST() {
      return 'sparta-content-manager-utility Host not provided to utility from site params';
    },
    ERROR_CONTENT_URL_INVALID(fetchUrl) {
      return `sparta-content-manager-utility Unable to pull data from a valid CDN with URL: ${fetchUrl}`;
    },
    ERROR_CONTENT_NOT_PROVIDED(options) {
      return `sparta-content-manager-utility content was not provided to function parameters: ${JSON.stringify(options)}`;
    },
    ERROR_UNDEFINED_CONTENT(fetchUrl) {
      return `sparta-content-manager-utility content response was null, undefined, or not found from URL: ${fetchUrl}`;
    },
    ERROR_KEY_OBJ_NOT_FOUND(contentKey) {
      return `sparta-content-manager-utility Content key was not found: ${contentKey}`;
    },
    ERROR_RECORD_VALUE_NOT_FOUND(record) {
      return `sparta-content-manager-utility Record value was not found: ${record}`;
    },
    ERROR_PARAMETER_NOT_DOM_ELEMENT() {
      return 'sparta-content-manager-utility The parameter provided was not an instanceof Element:';
    }
  }
};

/**
 * Store instance to hold the CMS items during the .init() function
 * Helps support multiple .init() calls to not re-trigger events
 * @constant {object}
 */
const cmsItemsInstance = {};
let activeFetchKeys = [];

/**
 * Assist the contentInjection function in adding content to DOM
 * @param {*} target DOM element where injection will take place
 * @param {*} value Value to be parsed
 * @param {*} dataOptions Options to determine how HTML is built
 */
function contentInjectionHelper({
  target,
  value,
  dataOptions,
  dataDynamicTokens
}) {
  let buildString = '';
  const {
    childWrapper = ''
  } = dataOptions;

  // String injection
  if (typeof value === 'string') {
    if (dataDynamicTokens) {
      value = replaceDynamicData({
        string: value,
        dataDynamicTokens
      });
    }
    buildString += `
      ${childWrapper ? `<${childWrapper}>` : ''}
        ${value}
      ${childWrapper ? `</${childWrapper}>` : ''}
    `.replace(/[\t\n\r]/gm, '').trim();
  }
  // Response is an array of string
  else if (Array.isArray(value)) {
    value.forEach((fetchItem, index) => {
      if (typeof fetchItem === 'string') {
        if (dataDynamicTokens) {
          fetchItem = replaceDynamicData({
            string: fetchItem,
            dataDynamicTokens
          });
        }
        const addSpacer = index !== value.length - 1;
        buildString += `
          ${childWrapper ? `<${childWrapper}>` : ''}
            ${fetchItem}${addSpacer ? '&nbsp;' : ''}
          ${childWrapper ? `</${childWrapper}>` : ''}
        `.replace(/[\t\n\r]/gm, '').trim();
      }
    });
  }
  // Response is an object
  else if (typeof value === 'object' && !Array.isArray(value) && value !== null) {
    console.log('Value is an object', value);
  }

  // Inject content
  if (target instanceof Element) {
    // Check if the record can be injected
    if (!target.getAttribute('data-content-inject') || target.getAttribute('data-content-inject') && target.getAttribute('data-content-inject').toString() !== 'false') {
      target.innerHTML = buildString;
    }
  }
}

/**
 * Replaces dynamic data within a string
 * @param {String} string Value to parse
 * @param {Object} dataDynamicTokens key/values to replace
 */
function replaceDynamicData({
  string,
  dataDynamicTokens
}) {
  const regexPattern = /\{([^}]+)\}/g;
  const matches = string.match(regexPattern) || [];

  // Loop through each bracketed item
  matches.forEach(matchItem => {
    if (matchItem.includes(DEFAULTS.ATTRIBUTES.dynamicDataHook)) {
      // cleanup
      const originalMatch = matchItem;
      matchItem = matchItem.replace(/[{}]/g, '');
      matchItem = matchItem.replace(DEFAULTS.ATTRIBUTES.dynamicDataHook, '');

      // split to get provided or default value
      matchItem = matchItem.split('|');
      matchItem = matchItem.filter(str => str != '');
      matchItem.forEach(item => {
        // Use value provided in JCR
        if (dataDynamicTokens[item]) {
          string = string.replace(originalMatch, dataDynamicTokens[item]);
        }
        // Use a default value if provided in CMS string
        else if (matchItem.length > 0) {
          string = string.replace(originalMatch, matchItem[1]);
        }
      });
    }
  });

  // Return string
  return string;
}

/**
 * Utility storage helper to create a series of empty objects for easier parsing
 * Example object output: {CMS_Shield.en.credit-score.score-factor = {}
 * @param {Object} settings Object of utility options
 * @param {String} key CMS url key param
 * @returns {Object} store object ready for using or parsing
 */
function readyStoreKey(settings, key) {
  const currentStore = spartaStoreUtility.getData(utilityNamespace) ? JSON.parse(spartaStoreUtility.getData(utilityNamespace)) : {};
  currentStore[settings.cmsName] = currentStore[settings.cmsName] || {};
  currentStore[settings.cmsName][key] = currentStore[settings.cmsName][key] || {};
  return currentStore;
}

/**
 * Looks up the Sparta CMS config options from numerous sources
 * @param {String} paramLookup Key to look up
 * @param {Object} config JCR json object
 * @param {Object} globalParams Page/Site params
 * @returns {String} account or player ID for Kaltura
 */
function getCmsParam(paramLookup, globalParams = spartaStoreUtility.getData('spaParams')) {
  if (paramLookup) {
    // Split param array to be parsed
    const lookupArray = paramLookup.split('.');

    // Reusable parse function to look through nested object to see if desired key/value exist
    function parseSpaParams(splitArray, data) {
      if (splitArray.length >= 2) {
        const key = splitArray[0];
        const value = splitArray[1];
        return data && data[key] && data[key][value] ? data[key][value] : undefined;
      }
      return undefined;
    }

    // Get params
    const siteParamLookup = parseSpaParams(lookupArray, globalParams.siteParams);
    const defaultsParamLookup = parseSpaParams(lookupArray, DEFAULTS);

    // Return results
    return siteParamLookup || defaultsParamLookup;
  }
  return undefined;
}

/**
 * Tests a string to see if it's a valid url
 * @param {*} str url string to test for validity
 * @returns {Boolean} true if valid url, false otherwise
 */
function validURL(str) {
  if (str.indexOf('localhost') !== -1) return true;
  const pattern = new RegExp('^(https?:\\/\\/)?' // protocol
  + '((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|' // domain name
  + '((\\d{1,3}\\.){3}\\d{1,3}))' // OR ip (v4) address
  + '(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*' // port and path
  + '(\\?[;&a-z\\d%_.~+=-]*)?' // query string
  + '(\\#[-a-z\\d_]*)?$', 'i'); // fragment locator
  return !!pattern.test(str);
}

/**
 * Constructs and tests validity of urls for calling later
 * @param {Object} settings Utility settings object
 * @param {String} key CMS key
 * @param {Object} options Override items in settings object
 * @returns {void}
 */
function constructUrl(settings, key, options) {
  let {
    cmsHost
  } = settings;
  let {
    segment
  } = settings;
  const {
    host
  } = settings;
  const {
    endpoint
  } = settings;
  const {
    language
  } = options || settings;
  if (!cmsHost) {
    cmsHost = ` ${host}/${endpoint}`;
  }
  cmsHost = cmsHost.replace('?key=', '');
  if (key) {
    if (!segment) {
      // Check for the presence of a segmwntation cookie
      const mlexpCookieVal = spartaGlobalFunctionsUtility.spartaGetCookie('MLEXP');

      // Make sure the segmentation cookie string has a valid JSON structure
      if (mlexpCookieVal) {
        try {
          // Decode the cookie value into the JSON string
          const mlexpStr = window.atob(mlexpCookieVal);

          // Parse the decoded JSON string
          const mlexpObj = JSON.parse(mlexpStr);

          // If the segmentation cookie has a "segment" property, with a "subGroup" property, convert it to lowercase and add it as a URL param
          if (mlexpObj && mlexpObj.segment) {
            segment = mlexpObj.segment.subGroup;
          }
        } catch (e) {
          console.error('sparta-content-manager-utility:consructUrl:"mlexp" cookie value is not formatted and/or encoded correctly');
        }
      }
    }
    const keyString = `${language}.${key}`;
    const constructedURL = `${cmsHost}?key=${keyString}${segment && typeof segment === 'string' && segment.length > 0 ? `&segment=${segment.toLowerCase()}` : ''}`;
    if (validURL(constructedURL)) {
      return constructedURL;
    }
  }
  return `${host}/${endpoint}?key=`;
}

/**
 * Reusable function to help setup host, endpoint, and cmsHost URL
 * @param {*} options
 * @returns
 */
function getCmsUrlBase(options) {
  const spaParams = spartaStoreUtility.getData('spaParams');
  const {
    enableProxy,
    siteParams
  } = spaParams;
  const hostname = siteParams.domain.hostname;
  const cmsName = options.cmsName || `CMS_${options.cmsName || 'Shield'}`;
  const language = options.language || spartaStoreUtility.getData('jcrLang');
  const host = enableProxy ? hostname : options.host || getCmsParam('cms.host') || getCmsParam('cdn.host');
  const endpoint = options.endpoint || getCmsParam('cms.endpoint') || getCmsParam('cdn.endpoint');
  const segment = options.segment;
  const cmsHost = constructUrl({
    host,
    endpoint,
    language
  });
  return {
    language,
    cmsHost,
    cmsName,
    host,
    endpoint,
    ...(segment && {
      segment
    })
  };
}

/**
 * Function to set default params for this component
 * @param {Object} options Settings to override the defaults
 * @returns {Object}
 */
function setDefaults(options = {}) {
  const settings = new function utilitySettingsConstructor() {
    // Attribute lookups
    const {
      selector,
      widgetInfo
    } = options;
    this.selector = selector || spartaStoreUtility.getData('applicationContainer', widgetInfo);
    if (!this.selector) {
      console.warn(DEFAULTS.STRINGS.ERROR_MISSING_CONTAINER());
      return false;
    }

    // Build array of CMS items from DOM
    const contentKeys = [...this.selector.querySelectorAll(`[${DEFAULTS.ATTRIBUTES.contentKey}]`)];
    this.contentKeys = {};
    contentKeys.forEach(element => {
      const key = element.getAttribute(`${DEFAULTS.ATTRIBUTES.contentKey}`);
      this.contentKeys[key] = this.contentKeys[key] || {};
      this.contentKeys[key].elements = this.contentKeys[key].elements || [];

      // Get data options from element
      const dataOptions = element.getAttribute(DEFAULTS.ATTRIBUTES.dataOptions) ? JSON.parse(element.getAttribute(DEFAULTS.ATTRIBUTES.dataOptions)) : {};

      // Get data options from element
      const dataDynamicTokens = element.getAttribute(DEFAULTS.ATTRIBUTES.dataDynamicTokens) ? JSON.parse(element.getAttribute(DEFAULTS.ATTRIBUTES.dataDynamicTokens)) : {};

      // Build elements array
      this.contentKeys[key].elements.push({
        element,
        dataOptions,
        dataDynamicTokens
      });

      // contentKey settings
      const status = this.contentKeys[key].status || DEFAULTS.STATUS.ready;

      // Check if any element does NOT want to pull from store and requires fresh data
      this.contentKeys[key].useCache = this.contentKeys[key].useCache && this.contentKeys[key].useCache.toString() === 'false' || dataOptions.useCache && dataOptions.useCache.toString() === 'false' ? 'false' : 'true';
      this.contentKeys[key] = {
        status,
        useCache: this.contentKeys[key].useCache,
        elements: this.contentKeys[key].elements,
        failedRecords: []
      };
    });
  }();
  if (settings.contentKeys) {
    cmsItemsInstance.contentKeys = settings.contentKeys;
    return settings;
  }
  return false;
}

/**
 * Determines if init settings or manual setContent promise `useCache` setting is true or false
 * @param {Object} settings Object with basic settings for utility
 * @returns {String} String version of boolean
 */
function useCacheSetting(settings) {
  const {
    key
  } = settings;
  let useCache = settings.contentKeys && settings.contentKeys[key].useCache.toString() === 'false' ? 'false' : 'true';
  useCache = settings.options && typeof settings.options.useCache !== 'undefined' && settings.options.useCache.toString() === 'false' ? 'false' : useCache;
  return useCache;
}

/**
 * Configures inputs for validation, formatting, obfuscation, etc.
 */
class SpartaContentManagerUtility {
  /**
   * Initializes a new instance of the class.
   * @param {object} options - Default options to be used by the utility
   * @returns {void}
   */
  init(options = {}) {
    // Create options object for utility methods
    let settings = setDefaults(options);
    if (settings === false) {
      console.warn(DEFAULTS.STRINGS.ERROR_MISSING_SETTINGS());
      return false;
    }
    const cmsBasics = getCmsUrlBase(settings);
    settings = {
      ...settings,
      ...cmsBasics
    };
    const self = this;

    // Kill check if no host provided
    if (!settings.host || !settings.cmsHost) {
      console.warn(DEFAULTS.STRINGS.ERROR_MISSING_SITE_HOST());
      return false;
    }

    // Loop through each key and get content from fetchUrl
    Object.keys(settings.contentKeys).forEach(key => {
      // Fetch content promise
      const retrieveContent = self.getContent({
        key,
        ...settings
      });

      // Process records and inject content
      retrieveContent.then(content => {
        // Check if content was successful or failed
        settings.contentKeys[key].status = typeof content === 'string' ? DEFAULTS.STATUS.failed : DEFAULTS.STATUS.complete;

        // Kill if failed
        if (settings.contentKeys[key].status === DEFAULTS.STATUS.failed) {
          return content;
        }

        // Get key and elements array
        const contentKey = settings.contentKeys[key];
        const {
          elements,
          failedRecords
        } = contentKey;

        // Loop through each Key DOM element
        elements.forEach(cmsItem => {
          const {
            element
          } = cmsItem;
          const record = element.getAttribute(DEFAULTS.ATTRIBUTES.contentRecord);
          const disableAutoInject = element.classList.contains(DEFAULTS.ATTRIBUTES.disableAutoInject) || element.getAttribute(DEFAULTS.ATTRIBUTES.disableAutoInject) === 'true';

          // Add record value
          try {
            const recordValue = record ? helpers.cmsDataParser(content, record) : undefined;
            if (recordValue) {
              cmsItem.recordValue = recordValue;
            }
          } catch (e) {}

          // Record lookup was attempted but failed
          if (record && !cmsItem.recordValue) {
            failedRecords.push(record);
            settings.contentKeys[key].status = DEFAULTS.STATUS.partialFailure;
            console.warn(DEFAULTS.STRINGS.ERROR_RECORD_VALUE_NOT_FOUND(record));
          }
          // Inject content if enabled
          else if (record && cmsItem.recordValue && !disableAutoInject) {
            helpers.contentInjection(cmsItem);
          }
        });

        // Update instance
        cmsItemsInstance.contentKeys = settings.contentKeys;

        // Return content from CDN
        return content;
      }).then(content => {
        // Publish event
        spartaEventsUtility.pub(`${DEFAULTS.EVENTS.CMS_Utility_Key_Complete}_${key}`, {
          key,
          ...(typeof content === 'string' && {
            error: content
          }),
          ...(typeof content === 'object' && {
            content
          }),
          status: settings.contentKeys[key].status,
          failedRecords: settings.contentKeys[key].failedRecords
        });
      });
    });
  }

  /**
   * Fetch content from URL and return its data and update store with its data
   * If content is sucessful pass it to success callback
   * If content is null/undefined pass it to failure callback
   * @param {Object} settings Object of utility options
   * @returns {Promise}
   */
  async getContent(settings) {
    // Check how fetch URLs are constructed
    // Depends on if this is part of init flow or not
    const cmsBasics = !settings.host || !settings.endpoint || !settings.language ? getCmsUrlBase(settings) : {
      language: settings.language,
      cmsHost: settings.cmsHost,
      cmsName: settings.cmsName,
      host: settings.host,
      endpoint: settings.endpoint,
      segment: settings.segment
    };

    // Kill check if no host provided
    if (!cmsBasics.host || !cmsBasics.cmsHost) {
      console.warn(DEFAULTS.STRINGS.ERROR_MISSING_SITE_HOST());
      return false;
    }

    // Build fetch settings
    const key = settings.key || settings.contentKey;
    const {
      cmsName
    } = cmsBasics;
    const fetchUrl = constructUrl(cmsBasics, key);

    // Get existing store
    let currentStore = spartaStoreUtility.getData(utilityNamespace) ? JSON.parse(spartaStoreUtility.getData(utilityNamespace)) : undefined;

    // Cache setting
    const useCache = useCacheSetting(settings);
    return new Promise(resolve => {
      // Check if we already have the data
      if (useCache && cmsName && currentStore && currentStore[cmsName] && currentStore[cmsName][key] && Object.keys(currentStore[cmsName][key]).length > 0) {
        const content = currentStore[cmsName][key];
        resolve(content);
      }
      // Fetch from CDN
      else {
        // Transport utility
        const transportRequest = spartaTransportUtility.subscribe({
          url: fetchUrl,
          cache: settings.cache || true,
          dataType: 'json',
          method: 'GET',
          jwt: settings.jwt || {},
          success: content => {
            // Valid content
            if (content) {
              // Setup store
              const setupStoreKey = readyStoreKey(cmsBasics, key);

              // if 'cmsName' is provided, cache the content
              if (cmsName && setupStoreKey[cmsName]) {
                try {
                  setupStoreKey[cmsName][key] = content;
                  currentStore = {
                    ...currentStore,
                    ...setupStoreKey
                  };
                  spartaStoreUtility.updateData(utilityNamespace, JSON.stringify(currentStore));
                } catch (e) {}
              }

              // Resolve content
              resolve(content);
            }
            // Undefiend or empty content
            else {
              resolve(DEFAULTS.STRINGS.ERROR_UNDEFINED_CONTENT(fetchUrl));
            }
          },
          // content was not found at CDN URL
          error: () => {
            resolve(DEFAULTS.STRINGS.ERROR_CONTENT_URL_INVALID(fetchUrl));
          },
          complete: content => {
            // Remove key from active fetching array
            activeFetchKeys = activeFetchKeys.filter(item => item !== key);
            resolve(content);
          }
        });

        // Prevent duplicate keys from firing multiple XHR request
        if (activeFetchKeys.includes(key)) {
          transportRequest.abort();
        }
        activeFetchKeys.push(key);
      }
    });
  }

  /**
   * Fetches content and auto injects the response token to a provided target
   * If content is sucessful pass it to success callback
   * If content is null/undefined pass it to failure callback
   * @param {Object} settings Object of utility options
   * @returns {Promise}
   */
  async getContentAndInject(settings = {}) {
    const {
      target,
      contentKey,
      contentRecord
    } = settings;
    if (target && contentKey && contentRecord) {
      return new Promise(resolve => {
        resolve(this.getContent(settings));
      }).then(response => {
        const recordValue = contentRecord.split('.').reduce((o, i) => o[i], response);
        helpers.contentInjection({
          recordValue,
          dataDynamicTokens: settings.dynamicTokens,
          dataOptions: settings.options
        }, {
          appendTarget: target
        });
        return {
          target,
          contentKey,
          contentRecord,
          response
        };
      });
    }
    return new Promise(resolve => {
      const errorMsg = DEFAULTS.STRINGS.ERROR_MISSING_REQUIRED_VARIABLES('target, contentKey, contentRecord', [`target: ${settings.target}`, `contentKey: ${settings.contentKey}`, `contentRecord: ${settings.contentRecord}`]);
      resolve(settings.options ? !settings.options.disableWarning ? console.log(errorMsg) : '' : console.log(errorMsg));
    });
  }

  /**
   * Object of functions that assist the utility run and get passed into event payloads
   * @returns {Object}
   */
  get cmsHelperFunctions() {
    return helpers;
  }
}

// Export new instance of the Sparta Utility.
var index = new SpartaContentManagerUtility();
const helpers = {
  /**
   * Searches response data for record value
   * @param {Object} data Fetch data to parse
   * @param {String} recordSearch A string of object keys seperated by a period
   * @example disclosures.zelle.html
   */
  cmsDataParser(data, recordSearch) {
    let filteredData;

    // Object was provided to function so attempt to process it
    if (recordSearch && typeof recordSearch === 'object') {
      const searchRecords = processKeyRecordIteration(recordSearch, [], 'push');
      searchRecords.forEach(record => {
        const recordValue = helpers.cmsDataParser(data, record);
        Object.keys(recordSearch).forEach(key => {
          const valueName = recordSearch[key].replaceAll(/[\[\]']+/g, '');
          if (valueName === record) {
            recordSearch[key] = recordValue;
          }
        });
      });
      return recordSearch;
    }
    // Process string
    else if (data && recordSearch) {
      const recordKeys = recordSearch.split('.');
      recordKeys.forEach(item => {
        filteredData = filteredData && item in filteredData ? filteredData[item] : item in data ? data[item] : undefined;
      });
      if (filteredData) {
        return filteredData;
      }
    }
    console.warn(DEFAULTS.STRINGS.ERROR_RECORD_VALUE_NOT_FOUND(recordSearch));
    return '';
  },
  /**
   * Injects parsed content into the DOM that has come from CMS
   * @param {*} cmsItem Object containing the CMS content
   * @param {*} options Object to override settings coming from cmsItem
   */
  contentInjection(cmsItem, options = {}) {
    // If we've been given a DOM element
    if (cmsItem instanceof Element) {
      const record = cmsItem.getAttribute(`[${DEFAULTS.ATTRIBUTES.contentRecord}]`);
      const {
        content
      } = options;

      // Kill check
      if (!content) {
        console.warn(DEFAULTS.STRINGS.ERROR_CONTENT_NOT_PROVIDED(options));
        return;
      }

      // Get data options from element
      const dataOptions = cmsItem.getAttribute(DEFAULTS.ATTRIBUTES.dataOptions) ? JSON.parse(cmsItem.getAttribute(DEFAULTS.ATTRIBUTES.dataOptions)) : {};

      // Get data options from element
      const dataDynamicTokens = element.getAttribute(DEFAULTS.ATTRIBUTES.dataDynamicTokens) ? JSON.parse(element.getAttribute(DEFAULTS.ATTRIBUTES.dataDynamicTokens)) : {};

      // Get record value if content was provided
      let recordValue;
      try {
        recordValue = record ? helpers.cmsDataParser(content, record) : undefined;
        if (recordValue) {
          cmsItem.recordValue = recordValue;
        }
      } catch (e) {}
      if (recordValue) {
        contentInjectionHelper({
          target: cmsItem,
          value: recordValue,
          dataOptions,
          dataDynamicTokens
        });
      } else if (record && !recordValue) {
        console.warn(DEFAULTS.STRINGS.ERROR_RECORD_VALUE_NOT_FOUND(record));
      }
    }
    // If we were given an object, test for CMS values
    else if (typeof cmsItem === 'object' && !Array.isArray(cmsItem) && cmsItem !== null && cmsItem.recordValue) {
      const {
        element
      } = cmsItem;
      const {
        recordValue = ''
      } = cmsItem;
      const {
        dataOptions = {}
      } = cmsItem;
      const {
        dataDynamicTokens = {}
      } = cmsItem;
      const target = options.appendTarget || element;
      if (recordValue) {
        contentInjectionHelper({
          target,
          value: recordValue,
          dataOptions,
          dataDynamicTokens
        });
      }
    }
    // We were given a string for `cmsItem` param
    else if (typeof cmsItem === 'string') {
      const target = options.appendTarget;
      if (target) {
        contentInjectionHelper({
          target,
          value: cmsItem,
          options
        });
      }
    }
  },
  /**
   * Provides the existing store data
   */
  getStoreData() {
    const storeData = spartaStoreUtility.getData(utilityNamespace) ? JSON.parse(spartaStoreUtility.getData(utilityNamespace)) : {};
    return storeData;
  },
  /**
   * Gets the object for a contentKey from the instance object
   * @param {*} contentKey String of the key to pull cmsItems from
   * @returns {Object} Object of contentKey cmsItems, status, etc
   */
  getKeyInstanceObj(contentKey) {
    if (typeof contentKey === 'string') {
      return cmsItemsInstance && cmsItemsInstance.contentKeys && cmsItemsInstance.contentKeys[contentKey] ? cmsItemsInstance.contentKeys[contentKey] : undefined;
    }
    console.warn(DEFAULTS.STRINGS.ERROR_KEY_OBJ_NOT_FOUND(contentKey), cmsItemsInstance);
    return undefined;
  },
  /**
   * Gets the cmsItem object from the cmsItemsInstance object
   * @param {*} element DOM element with valid contentKey on it
   * @returns {Object} the cmsItem object
   */
  getCmsItem(element) {
    if (element instanceof Element) {
      const contentKey = element.getAttribute(DEFAULTS.ATTRIBUTES.contentKey);
      const instanceKeys = cmsItemsInstance && cmsItemsInstance.contentKeys && cmsItemsInstance.contentKeys[contentKey] ? cmsItemsInstance.contentKeys[contentKey] : undefined;
      if (instanceKeys && instanceKeys.elements) {
        const getCmsItem = instanceKeys.elements.find(o => {
          if (o.element === element) {
            return o;
          }
        });
        return getCmsItem;
      }
      return undefined;
    }
    console.warn(DEFAULTS.STRINGS.ERROR_PARAMETER_NOT_DOM_ELEMENT, element);
    return undefined;
  }
};

export { index as default };
